import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useMediaQuery } from '../shared/useMediaQuery';
import Area0 from './Area0';
import Area1 from './Area1';
import Area2 from './Area2';
import Area3 from './Area3';
const FloorPlan = () => {
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [selectedArea, setSelectedArea] = useState(0);
    const [data, setData] = useState();
    const isSmallMobile = useMediaQuery('(max-width: 576px)');
    const swiperRef = useRef(null);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    let activeSlideIndex = data && selectedRoom ? data.rooms.findIndex(room => room.id === selectedRoom && room.area === selectedArea) : null;
    if (activeSlideIndex === -1) {
        activeSlideIndex = 0;
    }
    useEffect(() => {
        if (data && isSmallMobile && selectedRoom === null) {
            // Select a default room when the user switches to mobile view
            setSelectedArea(data.rooms[0].area);
            setSelectedRoom(data.rooms[0].id);
        }
    }, [isSmallMobile]);
    useEffect(() => {
        if (!data) {
            return;
        }
        const activeSlideId = data.rooms.findIndex(room => room.id === selectedRoom && room.area === selectedArea);
        if (swiperRef.current && activeSlideId !== -1 && activeSlideId !== swiperRef.current.realIndex) {
            swiperRef.current.slideTo(activeSlideId);
        }
    }, [data, selectedRoom, selectedArea]);
    /**
     * Change room functions
     */
    function changeRoom(change) {
        if (!data || activeSlideIndex === null) {
            return;
        }
        const newIndex = activeSlideIndex + change;
        if (newIndex >= 0 && newIndex < data.rooms.length) {
            const nextRoom = data.rooms[newIndex];
            setSelectedRoom(nextRoom.id);
            setSelectedArea(nextRoom.area);
        }
    }
    function nextRoom() {
        changeRoom(1);
    }
    function previousRoom() {
        changeRoom(-1);
    }
    /**
     * Listen to key events
     */
    function handleKeyUp(event) {
        if (event.key === 'ArrowLeft') {
            previousRoom();
        }
        else if (event.key === 'ArrowRight') {
            nextRoom();
        }
        else if (event.key === 'Escape') {
            setSelectedRoom(null);
        }
    }
    useEffect(() => {
        document.addEventListener('keyup', handleKeyUp);
        return () => {
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [activeSlideIndex]);
    /**
     * Get data
     */
    useEffect(() => {
        const dataRaw = document.getElementById('floor-plan-data')?.innerHTML;
        if (dataRaw) {
            setData(JSON.parse(dataRaw));
        }
    }, []);
    if (!data) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: 'floor-selection-container' },
            React.createElement("button", { className: classNames('floor-button', { 'is-active': selectedArea === 3 }), onClick: () => setSelectedArea(3) }, "Area 3"),
            React.createElement("button", { className: classNames('floor-button', { 'is-active': selectedArea === 2 }), onClick: () => setSelectedArea(2) }, "Area 2"),
            React.createElement("button", { className: classNames('floor-button', { 'is-active': selectedArea === 1 }), onClick: () => setSelectedArea(1) }, "Area 1"),
            React.createElement("button", { className: classNames('floor-button', { 'is-active': selectedArea === 0 }), onClick: () => setSelectedArea(0) }, "Area 0")),
        React.createElement("div", { className: classNames('areas-container', { 'is-safari': isSafari }) },
            React.createElement(Area0, { data: data, selectedRoom: selectedRoom, setSelectedRoom: setSelectedRoom, selectedArea: selectedArea }),
            React.createElement(Area1, { data: data, selectedRoom: selectedRoom, setSelectedRoom: setSelectedRoom, selectedArea: selectedArea }),
            React.createElement(Area2, { data: data, selectedRoom: selectedRoom, setSelectedRoom: setSelectedRoom, selectedArea: selectedArea }),
            React.createElement(Area3, { data: data, selectedRoom: selectedRoom, setSelectedRoom: setSelectedRoom, selectedArea: selectedArea })),
        React.createElement(Swiper, { spaceBetween: 16, slidesPerView: 1.1, onSlideChange: ({ activeIndex }) => {
                setSelectedArea(data.rooms[activeIndex].area);
                setSelectedRoom(data.rooms[activeIndex].id);
            }, breakpoints: { 992: { enabled: false } }, centeredSlides: true, autoHeight: true, className: 'slides-container', onInit: swiper => (swiperRef.current = swiper) }, data.rooms.flat().map(room => (React.createElement(SwiperSlide, { key: `${room.area}_${room.id}` },
            React.createElement("div", { className: classNames('room-modal', {
                    'is-active': selectedArea === room.area && selectedRoom === room.id,
                }) },
                room.image && React.createElement("img", { className: 'modal-image', src: room.image }),
                React.createElement("h3", null, room.title),
                room.description && React.createElement("div", { dangerouslySetInnerHTML: { __html: room.description } }),
                room.hasBookingButton && data.bookingLabel && data.bookingLink && (React.createElement("a", { href: data.bookingLink, className: 'btn is-secondary' }, data.bookingLabel)),
                React.createElement("div", { className: 'room-modal-actions' },
                    React.createElement("button", { "aria-label": data.locale === 'en' ? 'Previous room' : 'Vorheriger Raum', onClick: previousRoom, className: 'room-modal-action-btn', disabled: activeSlideIndex === 0 },
                        React.createElement("img", { width: 12, height: 12, src: '/assets/dist/images/icon-chevron-left-white.svg', alt: data.locale === 'en' ? 'Caret left icon' : 'Pfeil nach links Icon' })),
                    React.createElement("button", { "aria-label": data.locale === 'en' ? 'Next room' : 'Nächster Raum', onClick: nextRoom, className: 'room-modal-action-btn', disabled: activeSlideIndex === data.rooms.length - 1 },
                        React.createElement("img", { width: 12, height: 12, src: '/assets/dist/images/icon-chevron-right-white.svg', alt: data.locale === 'en' ? 'Caret right icon' : 'Pfeil nach rechts Icon' })),
                    React.createElement("button", { "aria-label": data.locale === 'en' ? 'Close modal' : 'Fenster schließen', onClick: () => setSelectedRoom(null), className: 'room-modal-action-btn' },
                        React.createElement("img", { width: 16, height: 16, src: '/assets/dist/images/icon-cross.svg', alt: data.locale === 'en' ? 'Close icon' : 'Schließen Icon' })))))))),
        React.createElement("div", { className: 'swiper-controls' },
            React.createElement("button", { onClick: () => (swiperRef.current ? swiperRef.current.slidePrev() : null), className: 'swiper-btn swiper-btn-prev' },
                React.createElement(CaretLeft, { size: 28 })),
            React.createElement("button", { onClick: () => (swiperRef.current ? swiperRef.current.slideNext() : null), className: 'swiper-btn swiper-btn-next' },
                React.createElement(CaretRight, { size: 28 })))));
};
export default FloorPlan;
